import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Kunde } from "@bidi-handel/core/schemas";
import { api } from "../api/client";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../app/TenantContext";
import { Icon } from "../shell/Icon";

/**
 * Anfrage-Upload-Page im Redesign-Look: 3 Steps (Kunde · Bauvorhaben ·
 * Dateien) im Haupt-Form, rechts ein Rail mit Checkliste + Pipeline-Vorschau.
 *
 * Funktional unverändert: POST /api/anfragen via multipart/form-data, bei
 * Erfolg → Navigate auf Detail.
 */

type Status = "idle" | "uploading" | "error";

const PIPELINE_PHASES = [
  "Anfrage-Intake",
  "Pfad-Typ",
  "LV-Positionen",
  "Konsolidierung",
  "Match",
  "Bepreisung",
  "Angebot",
];

export function NewAnfragePage() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const kundenRes = useApi(() => api.getKunden(tenantId), [tenantId]);
  const [selectedKunde, setSelectedKunde] = useState<Kunde | null>(null);
  const [kundeName, setKundeName] = useState("");
  const [kundeNr, setKundeNr] = useState("");
  const [bauvorhaben, setBauvorhaben] = useState("");
  const [standort, setStandort] = useState("");
  const [abgabeDatum, setAbgabeDatum] = useState("");
  const [freitext, setFreitext] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const aktiveKunden = useMemo(
    () =>
      kundenRes.state.status === "ok"
        ? kundenRes.state.data.kunden.filter((k) => k.active)
        : [],
    [kundenRes.state]
  );

  useEffect(() => {
    if (selectedKunde) {
      setKundeName(selectedKunde.name);
      setKundeNr(selectedKunde.kundeNummer);
    }
  }, [selectedKunde]);

  const formValid =
    kundeName.trim() !== "" && bauvorhaben.trim() !== "" && files.length > 0;

  function addFiles(newFiles: FileList | File[] | null) {
    if (!newFiles) return;
    const arr = Array.from(newFiles);
    setFiles((cur) => {
      const seen = new Set(cur.map((f) => `${f.name}-${f.size}`));
      const merged = [...cur];
      for (const f of arr) {
        const key = `${f.name}-${f.size}`;
        if (!seen.has(key)) merged.push(f);
      }
      return merged;
    });
  }
  function removeFile(idx: number) {
    setFiles((cur) => cur.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formValid) return;
    setStatus("uploading");
    setError(null);
    try {
      const fd = new FormData();
      fd.append("tenantId", tenantId);
      if (selectedKunde) fd.append("kundeId", selectedKunde.id);
      fd.append("kundeName", kundeName.trim());
      if (kundeNr.trim()) fd.append("kundeNr", kundeNr.trim());
      fd.append("bauvorhabenBezeichnung", bauvorhaben.trim());
      if (standort.trim()) fd.append("bauvorhabenStandort", standort.trim());
      if (abgabeDatum) {
        const iso = new Date(`${abgabeDatum}T12:00:00`).toISOString();
        fd.append("abgabeDatum", iso);
      }
      if (freitext.trim()) fd.append("freitext", freitext.trim());
      for (const f of files) {
        fd.append("files", f, f.name);
      }
      const res = await fetch("/api/anfragen", {
        method: "POST",
        body: fd,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const errText =
          (body as { hint?: string; error?: string; message?: string } | null)?.hint ??
          (body as { hint?: string; error?: string; message?: string } | null)?.error ??
          `HTTP ${res.status}`;
        throw new Error(errText);
      }
      const { anfrageId } = body as { anfrageId: string };
      navigate(`/anfragen/${anfrageId}`);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <form className="screen new-anfrage fade-up" onSubmit={handleSubmit}>
      <div className="detail-hero">
        <div className="detail-hero-l">
          <Link to="/anfragen" className="btn ghost">
            <Icon name="back" size={15} /> Inbox
          </Link>
          <div className="detail-id">
            <div className="uppercase-mini">/ ANFRAGE / NEU</div>
            <h1 className="detail-customer">Neue Anfrage</h1>
            <div className="detail-meta mono">
              <span className="bv-chip">Upload · GAEB · PDF · Excel</span>
              <span className="id-chip">Pipeline startet nach Anlegen</span>
            </div>
          </div>
        </div>
      </div>

      <div className="na-grid">
        <div className="na-form">
          <section className="block">
            <div className="block-head">
              <span className="step-num mono">01</span>
              <span className="uppercase-mini">// KUNDE</span>
              <span className="block-sep" />
              <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
                aus Stammdaten oder manuell
              </span>
            </div>

            <KundePicker
              value={selectedKunde}
              onChange={(k) => {
                setSelectedKunde(k);
                if (!k) {
                  setKundeName("");
                  setKundeNr("");
                }
              }}
              kunden={aktiveKunden}
              loading={kundenRes.state.status === "loading"}
            />

            {!selectedKunde && aktiveKunden.length > 0 && (
              <div className="na-or">
                <span className="na-or-line" />
                <span className="uppercase-mini">oder manuell eintragen</span>
                <span className="na-or-line" />
              </div>
            )}

            {!selectedKunde && (
              <div className="form-grid">
                <div className="field">
                  <label className="uppercase-mini">Kundenname *</label>
                  <input
                    type="text"
                    className="input"
                    value={kundeName}
                    onChange={(e) => setKundeName(e.currentTarget.value)}
                    required
                  />
                </div>
                <div className="field">
                  <label className="uppercase-mini">Kunden-Nr (optional)</label>
                  <input
                    type="text"
                    className="input mono"
                    value={kundeNr}
                    onChange={(e) => setKundeNr(e.currentTarget.value)}
                  />
                </div>
              </div>
            )}
          </section>

          <section className="block">
            <div className="block-head">
              <span className="step-num mono">02</span>
              <span className="uppercase-mini">// BAUVORHABEN</span>
            </div>
            <div className="form-grid">
              <div className="field full">
                <label className="uppercase-mini">Bauvorhaben-Bezeichnung *</label>
                <input
                  type="text"
                  className="input"
                  value={bauvorhaben}
                  onChange={(e) => setBauvorhaben(e.currentTarget.value)}
                  placeholder="z.B. EFH Sonnenberg H4"
                  required
                />
              </div>
              <div className="field">
                <label className="uppercase-mini">Standort (optional)</label>
                <input
                  type="text"
                  className="input"
                  value={standort}
                  onChange={(e) => setStandort(e.currentTarget.value)}
                  placeholder="Stadt / Adresse"
                />
              </div>
              <div className="field">
                <label className="uppercase-mini">Abgabe-Datum (optional)</label>
                <input
                  type="date"
                  className="input mono"
                  value={abgabeDatum}
                  onChange={(e) => setAbgabeDatum(e.currentTarget.value)}
                />
              </div>
              <div className="field full">
                <label className="uppercase-mini">Freitext / Notiz (optional)</label>
                <textarea
                  className="input"
                  rows={3}
                  value={freitext}
                  onChange={(e) => setFreitext(e.currentTarget.value)}
                  placeholder="Interne Notiz für die Anfrage …"
                  style={{ resize: "vertical", fontFamily: "var(--f-sans)" }}
                />
              </div>
            </div>
          </section>

          <section className="block">
            <div className="block-head">
              <span className="step-num mono">03</span>
              <span className="uppercase-mini">// DATEIEN *</span>
              <span className="block-sep" />
              <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
                Pfad A: GAEB-XML bevorzugt
              </span>
            </div>

            <div
              className={`drop-zone ${dragging ? "is-drag" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".xml,.x81,.x82,.x83,.x84,.x85,.x86,.gaeb,.pdf,.xlsx,.xls"
                hidden
                onChange={(e) => addFiles(e.currentTarget.files)}
              />
              <div className="drop-ico">
                <svg
                  viewBox="0 0 64 64"
                  width="64"
                  height="64"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 38v10a4 4 0 004 4h32a4 4 0 004-4V38" />
                  <path d="M32 8v32M20 20l12-12 12 12" />
                </svg>
                <div className="drop-ring" />
              </div>
              <div>
                <div className="drop-h">GAEB-XML · PDF · Excel hier ablegen</div>
                <div className="drop-s">oder klicken zum Auswählen · max 100 MB pro Datei</div>
              </div>
              <div className="drop-formats">
                <span className="pill">
                  <span className="mono">.xml</span>
                </span>
                <span className="pill">
                  <span className="mono">.x81 / x82</span>
                </span>
                <span className="pill">
                  <span className="mono">.pdf</span>
                </span>
                <span className="pill">
                  <span className="mono">.xlsx</span>
                </span>
              </div>
              <div className="drop-grid" aria-hidden />
            </div>

            {files.length > 0 && (
              <div className="file-list">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="file-row"
                    style={{ animationDelay: `${i * 30}ms` }}
                  >
                    <FileIcon name={f.name} />
                    <div>
                      <div className="file-name mono">{f.name}</div>
                      <div className="file-meta uppercase-mini">
                        {fmtBytes(f.size)} · {f.type || "auto"}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => removeFile(i)}
                    >
                      entfernen
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {error && (
            <div
              className="warn-line"
              style={{
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>Upload fehlgeschlagen:</strong>{" "}
              <span className="mono">{error}</span>
            </div>
          )}
        </div>

        <aside className="na-rail">
          <div className="block na-summary">
            <div className="block-head">
              <span className="uppercase-mini">// CHECKLIST</span>
            </div>
            <Check ok={!!(selectedKunde || kundeName.trim())} label="Kunde gewählt oder eingetragen" />
            <Check ok={!!bauvorhaben.trim()} label="Bauvorhaben benannt" />
            <Check
              ok={files.length > 0}
              label={`${files.length} Datei${files.length === 1 ? "" : "en"} hochgeladen`}
            />
            <div
              className="check-foot uppercase-mini"
              style={{ color: formValid ? "var(--ok)" : "var(--fg-dim)" }}
            >
              {formValid ? "BEREIT ZUM ANLEGEN" : "NOCH NICHT VOLLSTÄNDIG"}
            </div>
          </div>

          <div className="block">
            <div className="block-head">
              <span className="uppercase-mini">// PIPELINE-VORSCHAU</span>
            </div>
            <div className="na-pipe">
              {PIPELINE_PHASES.map((p, i) => (
                <div key={p} className="na-pipe-row">
                  <span className="na-pipe-num mono">{String(i).padStart(2, "0")}</span>
                  <span className="na-pipe-l">{p}</span>
                  <span className="na-pipe-dot" />
                </div>
              ))}
            </div>
            <div className="na-pipe-foot uppercase-mini">~12 s · pro Anfrage</div>
          </div>

          <div className="na-actions">
            <Link to="/anfragen" className="btn ghost">
              Abbrechen
            </Link>
            <button
              type="submit"
              className={`btn primary ${!formValid || status === "uploading" ? "disabled" : ""}`}
              disabled={!formValid || status === "uploading"}
            >
              {status === "uploading" ? (
                <>
                  <span className="spinner" /> Lade hoch …
                </>
              ) : (
                <>
                  <Icon name="play" size={14} /> Anlegen + Pipeline starten
                </>
              )}
            </button>
          </div>
        </aside>
      </div>
    </form>
  );
}

/**
 * Kunde-Picker mit Autocomplete + „Auswahl entfernen"-Fallback.
 */
function KundePicker({
  value,
  onChange,
  kunden,
  loading,
}: {
  value: Kunde | null;
  onChange: (k: Kunde | null) => void;
  kunden: Kunde[];
  loading: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return kunden.slice(0, 6);
    return kunden
      .filter((k) =>
        [k.kundeNummer, k.name, k.kategorie ?? "", k.adresse?.ort ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
      .slice(0, 20);
  }, [query, kunden]);

  if (value) {
    return (
      <div className="kunde-picked">
        <div>
          <div className="kp-tag uppercase-mini" style={{ color: "var(--ok)" }}>
            KUNDE AUS STAMMDATEN
          </div>
          <div className="kp-name">{value.name}</div>
          <div className="kp-meta mono">
            {value.kundeNummer}
            {value.kategorie ? ` · ${value.kategorie}` : ""}
            {value.adresse?.ort ? ` · ${value.adresse.ort}` : ""}
          </div>
        </div>
        <button type="button" className="btn ghost sm" onClick={() => onChange(null)}>
          Auswahl entfernen
        </button>
      </div>
    );
  }

  return (
    <div className="kunde-search">
      <div className="search-wrap" style={{ width: "100%" }}>
        <span className="search-ico">
          <Icon name="search" size={15} />
        </span>
        <input
          type="text"
          className="input search-input"
          placeholder={
            loading
              ? "Lade Kunden …"
              : kunden.length === 0
                ? "Noch keine Kunden gepflegt — manuell eintragen unten"
                : "Kundennr / Name / Ort suchen …"
          }
          value={query}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          disabled={loading || kunden.length === 0}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="kunde-dropdown">
          {filtered.map((k) => (
            <button
              key={k.id}
              type="button"
              className="kunde-drop-row"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(k);
                setQuery("");
                setOpen(false);
              }}
            >
              <div>
                <div className="kd-name">{k.name}</div>
                <div className="kd-meta mono">
                  {k.adresse?.ort ?? "—"}
                </div>
              </div>
              <div className="kd-r">
                <span className="kunde-bar mono">{k.kundeNummer}</span>
                {k.kategorie && (
                  <span className="kunde-bar uppercase-mini">{k.kategorie}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={`check-row ${ok ? "is-ok" : ""}`}>
      <div className="check-box">{ok && <Icon name="check" size={12} />}</div>
      <div>{label}</div>
    </div>
  );
}

function FileIcon({ name }: { name: string }) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  let color = "var(--acc)";
  let borderColor = "oklch(0.78 0.16 215 / 0.4)";
  if (ext === "pdf") {
    color = "var(--bad)";
    borderColor = "oklch(0.72 0.18 25 / 0.4)";
  } else if (ext === "xlsx" || ext === "xls") {
    color = "var(--ok)";
    borderColor = "oklch(0.78 0.16 152 / 0.4)";
  } else if (/^x8[1-6]$|^xml$|^gaeb$/.test(ext)) {
    color = "var(--acc)";
    borderColor = "oklch(0.78 0.16 215 / 0.4)";
  }
  return (
    <div className="file-ico-box" style={{ color, borderColor }}>
      <span className="file-ico-ext mono">.{ext}</span>
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
